import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import type { SimilarExample } from '@furni/shared';
import { formatSAR } from '../lib/format';

export interface RevealItem {
  image_url: string;
  title?: string;
  retailer?: string;
  price_sar?: number;
  emphasis?: boolean;
}

interface Props {
  items: RevealItem[];
  intervalMs?: number;
}

export function toRevealItems(
  examples: SimilarExample[],
  validUrls: Set<string>,
): RevealItem[] {
  return examples
    .filter((e) => e.image_url && validUrls.has(e.image_url))
    .map((e) => ({
      image_url: e.image_url as string,
      title: e.title,
      retailer: e.retailer,
      price_sar: e.price_sar,
    }));
}

export function RevealMontage({ items, intervalMs = 1100 }: Props) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (items.length < 2) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % items.length), intervalMs);
    return () => clearInterval(id);
  }, [items.length, intervalMs]);

  if (items.length === 0) return null;

  return (
    <div className="relative w-full max-w-xs aspect-[4/5] mx-auto">
      <AnimatePresence>
        {items.map((ex, i) => {
          const offset = (i - index + items.length) % items.length;
          if (offset > 2) return null;
          return (
            <motion.div
              key={`${ex.image_url}-${i}`}
              className="absolute inset-0 rounded-3xl overflow-hidden shadow-quote border border-oud-100 bg-oud-50"
              initial={{ opacity: 0, y: 40, scale: 0.94, rotate: 4 }}
              animate={{
                opacity: offset === 0 ? 1 : 0.5 - offset * 0.22,
                y: offset * -6,
                scale: 1 - offset * 0.03,
                rotate: offset * -2,
                zIndex: 10 - offset,
              }}
              exit={{ opacity: 0, y: -40, scale: 0.9, rotate: -4 }}
              transition={{ duration: 0.55, ease: [0.4, 0, 0.2, 1] }}
            >
              <img
                src={ex.image_url}
                alt=""
                className="w-full h-full object-cover"
                loading="eager"
              />
              {ex.retailer || ex.title || ex.price_sar ? (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-oud-900/90 via-oud-900/40 to-transparent p-4 pt-10">
                  {ex.retailer ? (
                    <p className="text-[10px] tracking-wider text-gold-soft font-bold uppercase">
                      {ex.retailer}
                    </p>
                  ) : null}
                  {ex.title ? (
                    <p className="text-sm font-bold text-sand line-clamp-1 mt-0.5">{ex.title}</p>
                  ) : null}
                  {ex.price_sar ? (
                    <p className="text-base font-black text-gold-soft mt-1">
                      {formatSAR(ex.price_sar)}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
